import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { ModalService } from '@src/shared/modal/modal.service';
import { DeviceGroupService } from '../../../../woo_services.module/DeviceGroupService';
import { ExtraProductFormatField, ProductFormatService } from '../../../../woo_services.module/ProductFormatService';
import { ExtraPublisherField, PublisherService } from '../../../../woo_services.module/PublisherService';
import { TranslationService } from '../../../../woo_services.module/TranslationService';
import { DeviceGroup, ProductFormat, Publisher, SlotType, wooId } from '../../../../woo_services.module/shared-types';
import { ProductsPageUtilityService } from '../../shared/products-page-utility-service';
import { BehaviorParams } from '../behaviors/behavior-form.component';
import { DeviceGroupParams } from './device-group-form.component';

type includedProductFormatSlotTypes = Record<wooId, Record<SlotType, boolean>>;

@Component({
  selector: 'manage-device-groups',
  templateUrl: './manage-device-groups.component.html',
  styleUrls: ['../../shared/table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageDeviceGroups implements OnInit {
  @Input('headerPos') parentHeader: HTMLElement;
  @Output() onSubmit = new EventEmitter<BehaviorParams>();
  @Output() onChanges = new EventEmitter<void>();

  show = {
    newDeviceGroupForm: false,
    estimatesHaveChanged: false,
    updatingEstimates: false,
    loading: true,
    isEditing: false,
  };

  readonly tableHeaders = [
    'Apparat',
    'Placering',
    'Publicist',
    'Preroll',
    'Midroll',
    'Postroll',
    'Preroll',
    'Midroll',
    'Postroll',
    'Redigera',
  ];
  readonly staticFields = 3;
  savingTableEdit = signal<boolean>(false);

  publishers = signal<Publisher[]>([]);
  selectedPublisher = signal<Publisher>(null);
  productFormats = signal<ProductFormat[]>([]);
  selectedProductFormat = signal<ProductFormat>(null);
  deviceGroups = signal<DeviceGroup[]>([]);
  selectedDeviceGroup = signal<DeviceGroup>(null);
  deviceRows = signal<DeviceRow[]>([]);
  includedProductFormatSlotTypes = signal<includedProductFormatSlotTypes>(null);
  translateProductFormat: (args: any) => string;
  constructor(
    private modalService: ModalService,
    private publisherService: PublisherService,
    private productFormatService: ProductFormatService,
    private deviceGroupService: DeviceGroupService,
    private utilityService: ProductsPageUtilityService,
    private translationService: TranslationService,
    private changeDetection: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.translateProductFormat = this.translationService.convertProductFormat;
    this.publisherService
      .getPublishers(ExtraPublisherField.productFormats, ExtraPublisherField.enabled)
      .then((publishers) => {
        this.publishers.set(publishers);
        this.publishers.update((f) => f.filter((fp) => fp.enabled));
      })
      .then(() => {
        return this.productFormatService
          .getProductFormats(ExtraProductFormatField.enabled, ExtraProductFormatField.slotTypes)
          .then((productFormats) => {
            this.productFormats.set(productFormats.filter((f) => f.enabled));
            this.includedProductFormatSlotTypes.set(
              this.utilityService.setIncludedProductFormatSlotTypes(this.productFormats()),
            );
          })
          .then(() => {
            return this.getAndBuildDeviceGroups();
          });
      })
      .finally(() => {
        this.show.loading = false;
      });
  }

  async getAndBuildDeviceGroups(): Promise<void> {
    return this.deviceGroupService.getDeviceGroups().then((groups) => {
      this.deviceGroups.set(groups);
      this.buildDeviceRows();
      this.changeDetection.markForCheck();
    });
  }

  setEditing(value: boolean): void {
    this.show.isEditing = value;
  }

  cancelEditing(): void {
    this.getAndBuildDeviceGroups();
    this.setEditing(false);
  }

  async save(): Promise<void> {
    const filterActive = this.selectedDeviceGroup() || this.selectedPublisher() || this.selectedProductFormat();
    const textBlocks = ['Är du säker på att du vill spara dina ändringar?'];
    if (filterActive) {
      textBlocks.push('Notera att även ändringar i filtrerade rader sparas.');
    }
    this.modalService
      .openConfirm({
        title: 'Bekräfta ändringar',
        texts: textBlocks,
        confirmLabel: 'Spara',
        cancelLabel: 'Avbryt',
      })
      .then((status) => {
        if (status.confirm) {
          const loaderModal = this.modalService.openLoader('Sparar ändringar');
          this.savingTableEdit.set(true);
          this.show.updatingEstimates = true;
          const allUpdates = [];
          this.deviceGroups().forEach((device) => {
            allUpdates.push(this.deviceGroupService.updateDeviceGroup(device));
          });
          Promise.all(allUpdates)
            .then(() => {
              this.show.estimatesHaveChanged = true;
            })
            .catch((err) => {
              this.modalService.openError(
                'Ajdå, något gick snett. Kunde inte spara ändringar.',
                this.translationService.convertError(err.error.error),
              );
            })
            .finally(() => {
              loaderModal.close();
              this.savingTableEdit.set(false);
              this.getAndBuildDeviceGroups();
              this.setEditing(false);
              this.show.updatingEstimates = false;
              this.changeDetection.markForCheck();
            });
        }
      });
  }
  async addDeviceGroup(deviceGroupParams: DeviceGroupParams): Promise<void> {
    this.deviceGroupService
      .addDeviceGroup(deviceGroupParams)
      .then((deviceGroup) => {
        this.show.newDeviceGroupForm = false;
        this.deviceGroups.update((arr) => [...arr, deviceGroup]);
        this.show.estimatesHaveChanged = true;
      })
      .then(() => {
        this.buildDeviceRows();
        this.changeDetection.markForCheck();
      });
  }

  rowVisible(obj: DeviceRow): boolean {
    const publisherFiltered = this.selectedPublisher() && obj.publisherId !== this.selectedPublisher().id;
    const deviceGroupFiltered = this.selectedDeviceGroup() && obj.deviceId !== this.selectedDeviceGroup().id;
    const productFormatFiltered =
      this.selectedProductFormat() && obj.productFormatId !== this.selectedProductFormat().id;
    return !(publisherFiltered || deviceGroupFiltered || productFormatFiltered);
  }

  showNewDeviceGroup(): void {
    this.show.newDeviceGroupForm = true;
  }

  hideNewDeviceGroup(): void {
    this.show.newDeviceGroupForm = false;
  }

  getDeviceGroup(id: wooId): DeviceGroup {
    return this.deviceGroups().find((device) => device.id === id);
  }
  private buildDeviceRows(): void {
    let deviceRows: DeviceRow[] = [];
    this.deviceGroups().map((deviceGroup) => {
      this.publishers().map((publisher) => {
        this.utilityService
          .getPublisherSpecificProductFormats(publisher, this.productFormats())
          .map((productFormat) => {
            deviceRows = [
              ...deviceRows,
              {
                device: deviceGroup.name,
                deviceId: deviceGroup.id,
                productFormat: productFormat.name,
                productFormatId: productFormat.id,
                publisherId: publisher.id,
                publisher: this.utilityService.getPublisherName(publisher.id, this.publishers()),
                sharesPreroll: deviceGroup.shares[publisher.id][productFormat.id].preroll,
                sharesMidroll: deviceGroup.shares[publisher.id][productFormat.id].midroll,
                sharesPostroll: deviceGroup.shares[publisher.id][productFormat.id].postroll,
                withdrawalLimitsPreroll: deviceGroup.withdrawal_limits[publisher.id][productFormat.id].preroll,
                withdrawalLimitsMidroll: deviceGroup.withdrawal_limits[publisher.id][productFormat.id].midroll,
                withdrawalLimitsPostroll: deviceGroup.withdrawal_limits[publisher.id][productFormat.id].postroll,
              },
            ];
          });
      });
    });
    this.deviceRows.set(this.sortRows(deviceRows));
  }

  private sortRows(rows: DeviceRow[]): DeviceRow[] {
    const sorted = rows.sort((a, b) => {
      if (a.publisher !== b.publisher) return a.publisher.localeCompare(b.publisher);
      if (a.device !== b.device) return a.device.localeCompare(b.device);
      return a.productFormat.localeCompare(b.productFormat);
    });
    return sorted;
  }
}

interface DeviceRow {
  device: string;
  deviceId: wooId;
  productFormat: string;
  productFormatId: wooId;
  publisher: string;
  publisherId?: wooId;
  sharesPreroll?: number;
  sharesMidroll?: number;
  sharesPostroll?: number;
  withdrawalLimitsPreroll?: number;
  withdrawalLimitsMidroll?: number;
  withdrawalLimitsPostroll?: number;
}
